import _classCallCheck from "C:\\Users\\benja\\Documents\\projects\\dev\\jam-pad-2\\node_modules\\babel-preset-react-app\\node_modules\\@babel\\runtime/helpers/esm/classCallCheck";

/**
 * @module encoding
 * Encodes numbers in little-endian order (least to most significant byte order)
 * This should be compatible with Golang's binary encoding (https://golang.org/pkg/encoding/binary/)
 * which is also used in Protocol Buffers.
 */
import * as buffer from './buffer.js';
var bits7 = 127;
var bits8 = 255;
/**
 * A BinaryEncoder handles the encoding to an Uint8Array.
 */

export var Encoder = function Encoder() {
  _classCallCheck(this, Encoder);

  this.cpos = 0;
  this.cbuf = new Uint8Array(100);
  /**
   * @type {Array<Uint8Array>}
   */

  this.bufs = [];
};
/**
 * @function
 * @return {Encoder}
 */

export var createEncoder = function createEncoder() {
  return new Encoder();
};
/**
 * The current length of the encoded data.
 *
 * @function
 * @param {Encoder} encoder
 * @return {number}
 */

export var length = function length(encoder) {
  var len = encoder.cpos;

  for (var i = 0; i < encoder.bufs.length; i++) {
    len += encoder.bufs[i].length;
  }

  return len;
};
/**
 * Transform to Uint8Array.
 *
 * @function
 * @param {Encoder} encoder
 * @return {Uint8Array} The created ArrayBuffer.
 */

export var toUint8Array = function toUint8Array(encoder) {
  var uint8arr = new Uint8Array(length(encoder));
  var curPos = 0;

  for (var i = 0; i < encoder.bufs.length; i++) {
    var d = encoder.bufs[i];
    uint8arr.set(d, curPos);
    curPos += d.length;
  }

  uint8arr.set(buffer.createUint8ArrayViewFromArrayBuffer(encoder.cbuf.buffer, 0, encoder.cpos), curPos);
  return uint8arr;
};
/**
 * Write one byte to the encoder.
 *
 * @function
 * @param {Encoder} encoder
 * @param {number} num The byte that is to be encoded.
 */

export var write = function write(encoder, num) {
  if (encoder.cpos === encoder.cbuf.length) {
    encoder.bufs.push(encoder.cbuf);
    encoder.cbuf = new Uint8Array(encoder.cbuf.length * 2);
    encoder.cpos = 0;
  }

  encoder.cbuf[encoder.cpos++] = num;
};
/**
 * Write one byte at a specific position.
 * Position must already be written (i.e. encoder.length > pos)
 *
 * @function
 * @param {Encoder} encoder
 * @param {number} pos Position to which to write data
 * @param {number} num Unsigned 8-bit integer
 */

export var set = function set(encoder, pos, num) {
  var buffer = null; // iterate all buffers and adjust position

  for (var i = 0; i < encoder.bufs.length && buffer === null; i++) {
    var b = encoder.bufs[i];

    if (pos < b.length) {
      buffer = b; // found buffer
    } else {
      pos -= b.length;
    }
  }

  if (buffer === null) {
    // use current buffer
    buffer = encoder.cbuf;
  }

  buffer[pos] = num;
};
/**
 * Write one byte as an unsigned integer.
 *
 * @function
 * @param {Encoder} encoder
 * @param {number} num The number that is to be encoded.
 */

export var writeUint8 = function writeUint8(encoder, num) {
  return write(encoder, num & bits8);
};
/**
 * Write one byte as an unsigned Integer at a specific location.
 *
 * @function
 * @param {Encoder} encoder
 * @param {number} pos The location where the data will be written.
 * @param {number} num The number that is to be encoded.
 */

export var setUint8 = function setUint8(encoder, pos, num) {
  return set(encoder, pos, num & bits8);
};
/**
 * Write two bytes as an unsigned integer.
 *
 * @function
 * @param {Encoder} encoder
 * @param {number} num The number that is to be encoded.
 */

export var writeUint16 = function writeUint16(encoder, num) {
  write(encoder, num & bits8);
  write(encoder, num >>> 8 & bits8);
};
/**
 * Write two bytes as an unsigned integer at a specific location.
 *
 * @function
 * @param {Encoder} encoder
 * @param {number} pos The location where the data will be written.
 * @param {number} num The number that is to be encoded.
 */

export var setUint16 = function setUint16(encoder, pos, num) {
  set(encoder, pos, num & bits8);
  set(encoder, pos + 1, num >>> 8 & bits8);
};
/**
 * Write two bytes as an unsigned integer
 *
 * @function
 * @param {Encoder} encoder
 * @param {number} num The number that is to be encoded.
 */

export var writeUint32 = function writeUint32(encoder, num) {
  for (var i = 0; i < 4; i++) {
    write(encoder, num & bits8);
    num >>>= 8;
  }
};
/**
 * Write two bytes as an unsigned integer at a specific location.
 *
 * @function
 * @param {Encoder} encoder
 * @param {number} pos The location where the data will be written.
 * @param {number} num The number that is to be encoded.
 */

export var setUint32 = function setUint32(encoder, pos, num) {
  for (var i = 0; i < 4; i++) {
    set(encoder, pos + i, num & bits8);
    num >>>= 8;
  }
};
/**
 * Write a variable length unsigned integer.
 *
 * Encodes integers in the range from [0, 4294967295] / [0, 0xffffffff]. (max 32 bit unsigned integer)
 *
 * @function
 * @param {Encoder} encoder
 * @param {number} num The number that is to be encoded.
 */

export var writeVarUint = function writeVarUint(encoder, num) {
  while (num >= 128) {
    write(encoder, 128 | bits7 & num);
    num >>>= 7;
  }

  write(encoder, bits7 & num);
};
/**
 * Write a variable length string.
 *
 * @function
 * @param {Encoder} encoder
 * @param {String} str The string that is to be encoded.
 */

export var writeVarString = function writeVarString(encoder, str) {
  var encodedString = unescape(encodeURIComponent(str));
  var len = encodedString.length;
  writeVarUint(encoder, len);

  for (var i = 0; i < len; i++) {
    // @ts-ignore
    write(encoder, encodedString.codePointAt(i));
  }
};
/**
 * Write the content of another Encoder.
 *
 * TODO: can be improved!
 *
 * @function
 * @param {Encoder} encoder The enUint8Arr
 * @param {Encoder} append The BinaryEncoder to be written.
 */

export var writeBinaryEncoder = function writeBinaryEncoder(encoder, append) {
  return writeUint8Array(encoder, toUint8Array(append));
};
/**
 * Append fixed-length Uint8Array to the encoder.
 *
 * @function
 * @param {Encoder} encoder
 * @param {Uint8Array} uint8Array
 */

export var writeUint8Array = function writeUint8Array(encoder, uint8Array) {
  var prevBufferLen = encoder.cbuf.length; // TODO: Append to cbuf if possible

  encoder.bufs.push(buffer.createUint8ArrayViewFromArrayBuffer(encoder.cbuf.buffer, 0, encoder.cpos));
  encoder.bufs.push(uint8Array);
  encoder.cbuf = buffer.createUint8ArrayFromLen(prevBufferLen);
  encoder.cpos = 0;
};
/**
 * Append an Uint8Array to Encoder.
 *
 * @function
 * @param {Encoder} encoder
 * @param {Uint8Array} uint8Array
 */

export var writeVarUint8Array = function writeVarUint8Array(encoder, uint8Array) {
  writeVarUint(encoder, uint8Array.byteLength);
  writeUint8Array(encoder, uint8Array);
};